import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref, watch } from 'vue';
import { hasNoComma, isDepartmentName, required } from '@/lib/rule';
import { DepartmentTreeNode } from '@/interfaces/model/general/department';
import { useDepartmentComposable } from '@/composables/useDepartment';
import DepartmentParentPicker from '@/views/pages/companyDepartments/DepartmentParentPicker.vue';
import {
  PreviewLine,
  buildSubtreePreview,
  collectInvalidParentIds,
  findNode,
  subtreeHeight,
} from '@/utils/departmentTree';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';


export default /*#__PURE__*/_defineComponent({
  __name: 'DepartmentEditDialog',
  props: {
    value: { type: Boolean },
    target: null,
    tree: null
  },
  emits: ["input", "updated"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: boolean): void;
  (e: 'updated'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: boolean;
  target?: DepartmentTreeNode;
  tree: DepartmentTreeNode[];
};

const m = defineMessages({
  descendantsNotSelectable: '※配下の部署は親部署として選択できません。',
  parentChangeMovesSubtree:
    '親部署を変更すると、当該部署とその配下の部署の所属先が変更されます。',
  before: '変更前',
  after: '変更後',
});





const { isUpdating, updateCompanyDepartment, moveCompanyDepartment } =
  useDepartmentComposable();

const isValidForm = ref(false);
const form = reactive<{ name: string; parentId: string | null }>({
  name: '',
  parentId: null,
});
/**
 * 名前入力に一度でもカーソルが入ったか。プレビューを出すきっかけに使う。
 *
 * フォーカスが外れても下げない。親部署のツリーをクリックした瞬間に
 * プレビューが消えると、変更前後を見比べている最中に内容が飛んでしまう。
 * ダイアログを開き直したときだけ戻す。
 */
const isNameTouched = ref(false);

// ダイアログを開くたびに編集対象の現在値へ戻す
watch(
  () => props.target,
  (target) => {
    form.name = target?.name ?? '';
    form.parentId = target?.parent_id ?? null;
    isNameTouched.value = false;
  },
  { immediate: true }
);

const invalidParentIds = computed(() =>
  props.target ? collectInvalidParentIds(props.target) : new Set<string>()
);

const targetHeight = computed(() =>
  props.target ? subtreeHeight(props.target) : 1
);

const isNameChanged = computed(
  () => !!props.target && form.name !== props.target.name
);
const isParentChanged = computed(
  () => !!props.target && form.parentId !== (props.target.parent_id ?? null)
);
const isDirty = computed(() => isNameChanged.value || isParentChanged.value);
const showPreview = computed(() => isDirty.value || isNameTouched.value);
const canSubmit = computed(
  () => isDirty.value && isValidForm.value && !!form.name
);

const parentName = (parentId: string | null): string | undefined => {
  if (!parentId) {
    return undefined;
  }
  return findNode(props.tree, parentId)?.name;
};

/**
 * プレビューの粒度は変更の種類で変える。
 *
 * - リネームだけ: 名前だけを並べる (配下は動かないので出しても情報にならない)
 * - 親の付け替え: 配下ごと移動することが要点なので、部分木を出す
 */
const beforePreview = computed((): PreviewLine[] => {
  if (!props.target) {
    return [];
  }
  if (!isParentChanged.value) {
    return [{ uuid: props.target.uuid, text: props.target.name, isRoot: true }];
  }
  return buildSubtreePreview(props.target, parentName(props.target.parent_id));
});

const afterPreview = computed((): PreviewLine[] => {
  if (!props.target) {
    return [];
  }
  if (!isParentChanged.value) {
    return [{ uuid: props.target.uuid, text: form.name, isRoot: true }];
  }
  // 名前の変更もプレビューに反映したいので、表示用に差し替えたノードを作る
  const preview: DepartmentTreeNode = { ...props.target, name: form.name };
  return buildSubtreePreview(preview, parentName(form.parentId));
});

const close = () => {
  if (isUpdating.value) {
    return;
  }
  emit('input', false);
};

/**
 * 名前と親はエンドポイントが別 (PUT はリネーム、PATCH .../position は移動) なので、
 * 変更のあった方だけを順に呼ぶ。
 */
const submit = async () => {
  if (isUpdating.value || !props.target) {
    return;
  }

  let isRenamed = false;
  if (isNameChanged.value) {
    const succeeded = await updateCompanyDepartment(props.target.uuid, {
      name: form.name,
    });
    // 失敗時は開いたままにする。移動は送らない
    if (!succeeded) {
      return;
    }
    isRenamed = true;
  }

  if (isParentChanged.value) {
    // 移動先の兄弟グループの末尾に置く
    const siblings = form.parentId
      ? findNode(props.tree, form.parentId)?.children ?? []
      : props.tree;
    const moved = await moveCompanyDepartment(props.target.uuid, {
      parent_id: form.parentId,
      position: siblings.length,
    });
    if (!moved) {
      // 名前だけ反映済みなら一覧を更新して閉じる。未反映なら開いたままにする
      if (isRenamed) {
        close();
        emit('updated');
      }
      return;
    }
  }

  close();
  emit('updated');
};

return { __sfc: true,m, props, emit, isUpdating, updateCompanyDepartment, moveCompanyDepartment, isValidForm, form, isNameTouched, invalidParentIds, targetHeight, isNameChanged, isParentChanged, isDirty, showPreview, canSubmit, parentName, beforePreview, afterPreview, close, submit, hasNoComma, isDepartmentName, required, DepartmentParentPicker, sharedMessages }
}

})